import { useNavigate } from "react-router-dom";
import {
  Box,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DateTime } from "luxon";
import { usePendingMonthSummary } from "../../api/queries";
import {
  makeDateString,
  type MonthlyPendingBucketDto,
  type PredefinedFilterDto,
} from "../../api/types";
import { buildPredefinedFilterUrl } from "../../logic/predefined-filter-url";

/// Display cap for the per-row count chip; matches the historic UI behavior
/// where a backlog still reads as a single tidy "50+" rather than a large
/// number that would dominate the row visually.
const DISPLAY_COUNT_CAP = 50;

export type PendingTransactionsSummaryProps = {
  filter: PredefinedFilterDto;
};

export const PendingTransactionsSummary = ({
  filter,
}: PendingTransactionsSummaryProps) => {
  const navigate = useNavigate();
  const { data } = usePendingMonthSummary(filter);

  const buckets = data?.buckets ?? [];
  if (buckets.length === 0) return null;

  const currentYear = DateTime.now().year;

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
        {buckets.map((bucket) => (
          <BucketRow
            key={`${bucket.year}-${bucket.month}`}
            bucket={bucket}
            currentYear={currentYear}
            onClick={() => navigate(buildBucketUrl(filter, bucket))}
          />
        ))}
      </Stack>
    </Paper>
  );
};

type BucketRowProps = {
  bucket: MonthlyPendingBucketDto;
  currentYear: number;
  onClick: () => void;
};

const BucketRow = ({ bucket, currentYear, onClick }: BucketRowProps) => (
  <ListItemButton onClick={onClick}>
    <ListItemText
      primary={`Pending transactions from ${formatBucketLabel(bucket, currentYear)}`}
    />
    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
      {formatCount(bucket.count)}
    </Typography>
    <ChevronRightIcon color="action" />
  </ListItemButton>
);

/// Returns the month-name shortcut for a bucket. Year is appended whenever it
/// differs from `currentYear` so December 2025 reads unambiguously in January.
const formatBucketLabel = (
  bucket: MonthlyPendingBucketDto,
  currentYear: number,
): string => {
  const dt = DateTime.fromObject({ year: bucket.year, month: bucket.month });
  return dt.toFormat(bucket.year === currentYear ? "LLLL" : "LLLL yyyy");
};

const formatCount = (n: number): string =>
  n >= DISPLAY_COUNT_CAP ? `${DISPLAY_COUNT_CAP}+` : `${n}`;

const buildBucketUrl = (
  filter: PredefinedFilterDto,
  bucket: MonthlyPendingBucketDto,
): string => {
  const start = DateTime.fromObject({
    year: bucket.year,
    month: bucket.month,
  }).startOf("month");
  const end = start.endOf("month");
  return buildPredefinedFilterUrl(filter, {
    startDate: makeDateString(start.toISODate() ?? ""),
    endDate: makeDateString(end.toISODate() ?? ""),
  });
};
